import { useState, useEffect, FC } from "react";
import { useSettingsContext } from "../../../Settings/hooks/useSettingsContext";
import { useTranslation } from "react-i18next";
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import { 
  MenuItem, Select, FormControl, InputLabel, Box, Grid,
  SelectChangeEvent
} from "@mui/material";
import { default as contractSelectService } from "../services/contractSelect";
import { ContractListInfo, ContractInfo, AvailableContractTypes, ContractSelect } from "../types/contractSelectFields";

interface ContractSelectFieldParams {
  onChange: (newValue: ContractSelect) => void;
  value?: ContractSelect;
  hideIfEmpty?: boolean;
  readOnly?: boolean;
}

const ContractSelectField: FC<ContractSelectFieldParams> = ({ 
  onChange,
  value = undefined,
  hideIfEmpty = false,
  readOnly = false, 
}) => {

  const Settings = useSettingsContext();
  const { t } = useTranslation();
  const theme = useTheme();
  const breakpointUpMd = useMediaQuery(theme.breakpoints.up('md'));

  const [ state, setState ] = useState<"uninitialized" | "fetching" | "success" | "error">("uninitialized");
  const [ availableItems, setAvailableItems ] = useState<ContractListInfo["result"] | undefined>();
  const [ selectedItem, setSelectedItem ] = useState<ContractInfo | undefined>();
  const [ contractType, setContractType ] = useState<AvailableContractTypes>(Settings.features?.displayTypes[0] as AvailableContractTypes);
  
  const handleChange = (event: SelectChangeEvent) => {
    const newSelectedItem = availableItems![contractType]!.find(item => item.id.toString() == event.target.value)!;
    setSelectedItem(newSelectedItem);
    onChange({...newSelectedItem, type: contractType});
  }

  const changeContractType = (event: SelectChangeEvent) => {
    const value = event.target.value as AvailableContractTypes;
    const newSelectedItem = availableItems![value]![0];
    setContractType(value);
    setSelectedItem(newSelectedItem);
    onChange({...newSelectedItem, type: value});
  }

  useEffect(() => {
    setState("fetching")
    contractSelectService.getContractList()
    .then( response => {
      if (response.status === 200) {
        setState("success");
        const newItems = response.data?.result;
        const newContractType = Object.keys(newItems)[0] as AvailableContractTypes;
        const newSelectedItem = newItems[newContractType]![0];
        setAvailableItems(newItems);
        setSelectedItem(newSelectedItem);
        onChange({...newSelectedItem, type: newContractType});
      }
    })
  }, [])

  const containsItems = Settings.features?.displayTypes.length > 1 && 
    (availableItems?.["electricity"]?.length ?? 0) >= 1 &&
    (availableItems?.["gas"]?.length ?? 0) >= 1;

  return ( !(hideIfEmpty && !containsItems) &&
    <Box>
      <Grid container spacing={2}>
        {containsItems &&
          <Grid item xs={ breakpointUpMd ? 2 : 12 }>
            <InputLabel shrink htmlFor="">
              {(t('common:text.contact_type_contract')) + "*"}
            </InputLabel>
            <FormControl fullWidth={true}
              disabled={readOnly}
            >
              <Select
                value={contractType}
                onChange={changeContractType}
                style={{textTransform: "capitalize"}}
              >
                <MenuItem value={"electricity"} style={{textTransform: "capitalize"}}>{t('common:content_types.electricity')}</MenuItem>
                <MenuItem value={"gas"} style={{textTransform: "capitalize"}}>{t('common:content_types.gas')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        }

        <Grid 
          item 
          xs={
            containsItems &&
            breakpointUpMd ? 10 : 12 
          }
        >
          <InputLabel shrink htmlFor="">
            {t('common:text.contact_contract') + "*"}
          </InputLabel>
          <FormControl fullWidth={true}
            disabled={readOnly && state == "fetching"}
          >
            <Select
              value={selectedItem?.id.toString() ?? ""}
              onChange={handleChange}
            >
              {availableItems && availableItems[contractType]!.map((contract) => {
                return <MenuItem key={contract.id} value={contract.id}>{contract.name}</MenuItem>
              })}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ContractSelectField;
